import React, { useState, useEffect, useMemo } from 'react';
import { NetworkManager } from '../../../Components/NetworkManager';
import ImageModal from '../../../Components/ImageModal';
import ToastManager from '../../../Components/ToastManager';
import { CommonUtil } from '../../../utils/CommonUtil';
import GenericPopup from '../../../Components/GenericPopup';
import MultiEmailInput from '../../../Components/MultiEmailInput';
import { useNavigate } from 'react-router';
import ImageLoaderNew from '../../../Components/ImageLoaderNew';
import DropdownMenu from '../../../Components/DropdownMenu';

const InspectedImages = ({ data, imageCount, show }: any) => {
  const navigate = useNavigate();
  const [localData, setLocalData] = useState<any[]>([]);
  const [draggedItem, setDraggedItem] = useState<{ docIndex: number; imgIndex: number } | null>(null);
  const [selectedImages, setSelectedImages] = useState<any[]>([]);
  const [selectAllState, setSelectAllState] = useState<boolean | 'indeterminate'>(false);
  const [galleryPop, setGalleryPop] = useState(false);
  const [sharePop, setSharePop] = useState(false);
  const [hoveredImagePath, setHoveredImagePath] = useState<string | null>(null);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectOptions, setSelectOptions] = useState<{ label: string; value: string }[]>([]);
  const [orderedImageUrls, setOrderedImageUrls] = useState<any>([]);
  
  useEffect(() => {
    if (data) {
      console.log("Initial raw data received:", data);
      const enriched = data.map((doc: any) => ({
        ...doc,
        Images: doc.Images?.map((img: any, idx: number) => ({
          ...img,
          imgSeqId: img.imgSeqId ?? idx
        })) || []
      }));
      setLocalData(enriched);
    }
  }, [data]);

  useEffect(() => {
    if (localData.length > 0) {
      const sequenceLog = localData.map(doc => ({
        docType: doc.docTypeName,
        images: doc.Images.map((img: any) => ({
          name: extractFileNameWithoutExtension(img.img),
          seqId: img.imgSeqId
        }))
      }));
      console.log("Current Image Sequence (localData):", sequenceLog);
    }
  }, [localData]);

  useEffect(() => {
    const all = localData?.flatMap((doc: any) =>
      doc.Images?.map((i: any) => i?.img)?.filter(Boolean)
    ) || [];

    if (selectedImages.length === 0) {
      setSelectAllState(false);
    } else if (selectedImages.length === all.length) {
      setSelectAllState(true);
    } else {
      setSelectAllState('indeterminate');
    }
  }, [selectedImages, localData]);

  
  useEffect(() => {
    if (!show) {
      setOrderedImageUrls([]);
      setSelectedImages([]);
      setLocalData([]);
    }
  }, [show]);

  const allImagesFlat = useMemo(() => {
    return localData?.flatMap((doc: any) => 
      doc.Images?.map((i: any) => i?.img)?.filter(Boolean)
    ) || [];
  }, [localData]);

  const sortedSelectedForShare = useMemo(() => {
    return selectedImages.slice().sort((a, b) => {
      const indexA = allImagesFlat.indexOf(a.img);
      const indexB = allImagesFlat.indexOf(b.img);
      return indexA - indexB;
    });
  }, [selectedImages, allImagesFlat]);

  if (!show) return null; 

  const toggleSelectAll = () => {
    const allImages = localData?.flatMap((doc: any) =>
      Array.isArray(doc.Images)
        ? doc.Images
            .filter((i: any) => i?.img)
            .map((i: any) => ({ img: i.img, comment: i.comment || "" }))
        : []
    ) || [];

    if (selectedImages.length === allImages.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(allImages);
    }
  };


  const toggleDocumentSelection = (doc: any) => {
    const docImages = doc.Images?.filter((i: any) => i?.img)
      .map((i: any) => ({ img: i.img, comment: i.comment || "" })) || [];

    const allSelected = docImages.every((imgObj: any) =>
      selectedImages.some((sel: any) => sel.img === imgObj.img)
    );

    if (allSelected) {
      setSelectedImages(prev =>
        prev.filter(sel => !docImages.some((imgObj: any) => imgObj.img === sel.img))
      );
    } else {
      setSelectedImages(prev => {
        const combined = [...prev, ...docImages];
        const unique = Array.from(
          new Map(combined.map(item => [item.img, item])).values()
        );
        return unique;
      });
    }
  };

  const toggleImageSelection = (imgObj: any) => {
    setSelectedImages(prev => {
      const exists = prev.some(sel => sel.img === imgObj.img);
      if (exists) {
        return prev.filter(sel => sel.img !== imgObj.img);
      }
      return [...prev, { img: imgObj.img, comment: imgObj.comment || "" }];
    });
  };

  const isDocumentSelected = (doc: any) => {
    const docImages = doc.Images?.filter((i: any) => i?.img) || [];
    return (
      docImages.length > 0 &&
      docImages.every((imgObj: any) =>
        selectedImages.some((sel: any) => sel.img === imgObj.img)
      )
    );
  };


  const isDocumentIndeterminate = (doc: any) => {
    const docImages = doc.Images?.filter((i: any) => i?.img) || [];
    const someSelected = docImages.some((imgObj: any) =>
      selectedImages.some((sel: any) => sel.img === imgObj.img)
    );
    const allSelected = docImages.every((imgObj: any) =>
      selectedImages.some((sel: any) => sel.img === imgObj.img)
    );
    return someSelected && !allSelected;
  };

  const handleDownload = async (option: any) => {
    if (isDownloading) return;

    setIsDownloading(true);

    const url = option === "as Zip" ? `api/session/download-zip` : `api/V2InspectionDocuments/getInspectionSummaryPdf?docId=${CommonUtil.getInterchangeDocumentId() !== null ? CommonUtil.getInterchangeDocumentId(): localData?.[0]?.documentId}`;
    let myHeaders = new Headers();
    myHeaders?.append('Content-Type', 'application/json');

    if (localStorage.getItem('token')) {
      const token = 'Bearer ' + localStorage.getItem('token');
      myHeaders?.append('Authorization', token);
    }
    try {
      const sortedSelected = selectedImages.slice().sort((a, b) => {
        const indexA = allImagesFlat.indexOf(a.img);
        const indexB = allImagesFlat.indexOf(b.img);
        return indexA - indexB;
      });

      const response = await fetch(NetworkManager.serverPath + url, {
        method: 'POST',
        headers: myHeaders,
        body: JSON.stringify(sortedSelected),
      });
      
      if (!response.ok) {
        throw new Error('Download failed with status ' + response.status);
      } 

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${localData?.[0]?.vinNumber}_${selectedImages?.length}_Images_${option === "as Zip"? ".zip" : ".pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      ToastManager.showToast("Download failed", "Error");
    } finally {
      setIsDownloading(false);
      ToastManager.showToast(`${selectedImages?.length} ${selectedImages.length > 1 ? "Pictures" : "Picture"} have been downloaded successfully ${option} file`, "Info")
    }
  };

  const onDragStart = (docIndex: number, imgIndex: number) => {
    setDraggedItem({ docIndex, imgIndex });
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const onDrop = (targetDocIndex: number, targetImgIndex: number) => {
    if (!draggedItem) return;

    const { docIndex: sourceDocIndex, imgIndex: sourceImgIndex } = draggedItem;

    // Only allow reordering within the same document grouping for now
    if (sourceDocIndex !== targetDocIndex) {
      setDraggedItem(null);
      return;
    }

    const newData = [...localData];
    const docImages = [...newData[sourceDocIndex].Images];
    const [movedItem] = docImages.splice(sourceImgIndex, 1);
    docImages.splice(targetImgIndex, 0, movedItem);

    // Update imgSeqId based on new positions
    newData[sourceDocIndex].Images = docImages.map((img: any, idx: number) => ({
      ...img,
      imgSeqId: idx
    }));

    setLocalData(newData);
    setDraggedItem(null);
  };


  const openSharePop = () => {
    setSharePop(true);
    setSelectOptions([]);
  };

  const Loader = () => (
    <div className="">
      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
        Downloading Report...
    </div>
  );

  const extractFormattedDateTime = (filename: string) => {
    // Try to match the format: yyyyMMdd-HHmmss
    const match1 = filename.match(/(\d{8}-\d{6})/);
    if (match1) return match1[1];

    // Try to match the format: ddMMyyyy_yyyy-MM-dd_HH-mm-ss
    const match = filename.match(/(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      return `${year}${month}${day}-${hour}${minute}${second}`;
    }

    return null;
  }

  
  const extractFileNameWithoutExtension = (path: string): string => {
    const parts = path.split('/');
    const fileName = parts[parts.length - 1];
    return fileName.replace(/\.[^/.]+$/, '');
  };

  return (
    <div style={{ overflowX: "hidden", overflowY: "hidden", height: "100%" }}>
      <div className="col-lg-12  inspected-images-container py-3 px-0" style={{height: `${window.innerHeight - 170}px`}}>
        <div className="col-12 header-row">
          <div className="equipment-id">Vin Number: {localData?.[0]?.vinNumber}</div>
        </div>
        <div className='col-12 main-header'>
          <div className="col-6 ms-4 d-flex justify-content-start align-items-center">
            <div className="mt-1 me-2 d-flex align-items-center">
              <input
                role="button"
                type="checkbox"
                id="selectAll"
                disabled={imageCount === 0}
                checked={selectAllState === true}
                ref={input => {
                  if (input) {
                    input.indeterminate = selectAllState === 'indeterminate';
                  }
                }}
                onChange={toggleSelectAll}
                style={{ marginTop: "4px" }}
              />
              <label className='select-all mb-0 ms-2 me-2' >Select All</label>
            </div>
            <div className="selected-count rounded-pill border"> {selectedImages.length} {`${selectedImages.length > 1 ? "Pictures" : "Picture"}`} Selected 
              <span role="button" onClick={()=>setSelectedImages([])}><i className="fa-solid fa-xmark mx-1"></i></span>
            </div>
          </div>

          <div className="col-6 pe-5 d-flex justify-content-end">
            <button className="btn-green me-2" onClick={openSharePop} disabled={selectedImages.length === 0}>Share</button>
            <DropdownMenu
              label="Download"
              options={[
                { label: "as PDF", value: "as PDF" },
                { label: "as Zip", value: "as Zip" },
              ]}
              isDisabled={selectedImages.length === 0 || isDownloading}
              onOptionClick={(option: any) => handleDownload(option.value)}
            />
          </div>
        </div>
        <div className='col-12 ' style={{height: `${window.innerHeight - 170}px`, overflowX: "hidden", overflowY: "scroll"}}>
          {localData?.map((doc: any, i: any) => (
            <div className='col-12 ' key={`${doc.docTypeName}-${doc.documentId}-${i}`}>
              <div className="col-12 grid-header">
                <input
                  role="button"
                  type="checkbox"
                  disabled={imageCount === 0}
                  id={`doc-${doc.docTypeName}-${doc.documentId}-${i}`}
                  checked={isDocumentSelected(doc)}
                  ref={input => {
                    if (input) input.indeterminate = isDocumentIndeterminate(doc);
                  }}
                  onChange={() => toggleDocumentSelection(doc)}
                />
                <label htmlFor={`doc-${doc.docTypeName}-${doc.documentId}`}>
                  {doc.docTypeName} {doc.EquipmentNo}
                </label>
                <span className='ms-2'>{doc.Images?.length || 0}</span>
              </div>
              <div className="col-12 ps-5 pe-3 d-flex flex-wrap justify-content-start align-items-start mb-4">
                {doc.Images?.map((img: any, index: number) => {
                  const globalIndex = allImagesFlat.findIndex((val: any, idx: any) => val === img?.img && idx >= index); 
              
                  return (
                    <div
                      key={img?.img}
                      className="p-2"
                      style={{ 
                        width: "16.66%", 
                        opacity: draggedItem?.docIndex === i && draggedItem?.imgIndex === index ? 0.5 : 1,
                        cursor: "move"
                      }}
                      draggable
                      onDragStart={() => onDragStart(i, index)}
                      onDragOver={onDragOver}
                      onDrop={() => onDrop(i, index)}
                      onMouseEnter={() => setHoveredImagePath(img?.img)}
                      onMouseLeave={() => setHoveredImagePath(null)}
                    >
                      <div style={{ position: "relative" }}>
                        {hoveredImagePath === img?.img && (
                          <div 
                            style={{ 
                              position: "absolute", 
                              top: "5px", 
                              right: "5px", 
                              zIndex: 10,
                              background: "rgba(255, 255, 255, 0.7)",
                              padding: "1px 4px",
                              borderRadius: "3px",
                              pointerEvents: "none"
                            }}
                          >
                            <i className="fas fa-grip-vertical"></i>
                          </div>
                        )}
                        <div
                          className="card"
                          role="button"
                          onClick={() => {
                            setGalleryPop(true);
                            setStartIndex(globalIndex);
                          }}
                        >
                          <div
                            style={{
                              flex: "1",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <ImageLoaderNew
                              key={img?.img}
                              path={img?.img}
                              index={globalIndex}
                              onLoad={(idx: number, url: string) => {
                                setOrderedImageUrls((prev: any) => {
                                  const updated = [...prev];
                                  if (updated[idx]?.original === url) return prev; // Avoid redundant state update
                                  updated[idx] = { key: img?.img, original: url, comment: img?.comment ? img?.comment : null };
                                  return updated;
                                });
                              }}
                            />
                          </div>
                          <small className="card-footer fst-100 p-0 text-center">
                            {extractFileNameWithoutExtension(img?.img)} (Seq: {img.imgSeqId + 1})
                          </small>
                        </div>
                        <input
                          role="button"
                          type="checkbox"
                          id={`img-${doc.docTypeName}-${doc.documentCode}-${index}`}
                          checked={selectedImages?.some(sel => sel.img === img?.img)}
                          disabled={imageCount === 0 || !img?.img}
                          onChange={() => toggleImageSelection(img)}
                          style={{ position: "absolute", top: "8px", left: "8px" }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <ImageModal
        isOpen={galleryPop}
        imageSrcs={orderedImageUrls}
        startIndex={startIndex}
        caption={""}
        onClose={() => {
          setGalleryPop(false);
          setStartIndex(0);
        }}
      />
      {sharePop && (
        <GenericPopup
          header="Share Pictures"
          mode="Share"
          onClose={()=>setSharePop(false)}
          className="share-modal-size"
        >{sharePop && 
          <MultiEmailInput
            value={selectOptions}
            loadedData={localData}
            selectedImages={sortedSelectedForShare}
            placeholder="Enter email id (e.g., name@example.com)."
            onClose={()=>setSharePop(false)}
          />}
        </GenericPopup>
      )}
      {isDownloading && (
        <GenericPopup
          mode="Share"
          onClose={()=>setSharePop(false)}
          size="sm"
          action={false}
        ><Loader/>
        </GenericPopup>
      )}
    </div>
  );
};

export default InspectedImages;